import { FILE_TYPE_PDF } from '@constants/files.ts';
import { BREAKPOINTS } from '@constants/layout.ts';
import { openInNewTab } from '@utils/navigation.ts';
import type { PDFDocumentProxy } from 'pdfjs-dist';

export const computeFitWidthScale = async (
    pdf: PDFDocumentProxy,
    pageNum: number,
    rotation: number,
    containerWidth: number,
): Promise<number> => {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1, rotation });
    return containerWidth / viewport.width;
};

export const computeFitPageScale = async (
    pdf: PDFDocumentProxy,
    pageNum: number,
    rotation: number,
    containerWidth: number,
    containerHeight: number,
): Promise<number> => {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1, rotation });
    return Math.min(containerWidth / viewport.width, containerHeight / viewport.height);
};

export const parsePageFromHash = (hash: string, pdfPages: number): number | null => {
    const query = hash.startsWith('#') ? hash.slice(1) : hash;
    const params = new URLSearchParams(query);
    const page = parseInt(params.get('page') || '', 10);
    return page >= 1 && page <= pdfPages ? page : null;
};

export const READER_WRAPPER_ID = 'reader-wrapper';

export const getSidebarPageScrollerHeight = (fallbackHeight: number, isFullscreen = false): number => {
    const SCROLLER_HEADER_HEIGHT = 57;
    const SCROLLER_PADDINGS = 16;
    const PAGE_MARGIN = 16;
    const MIN_VIRTUAL_LIST_HEIGHT = 200;

    const mainWrapper = document.getElementById(READER_WRAPPER_ID);

    if (!mainWrapper || isFullscreen) {
        return fallbackHeight;
    }

    const rect = mainWrapper.getBoundingClientRect();
    const available = fallbackHeight - rect.top - SCROLLER_HEADER_HEIGHT - SCROLLER_PADDINGS - PAGE_MARGIN;
    return Math.max(available, MIN_VIRTUAL_LIST_HEIGHT);
};

// Modern browser detection (fetch + fullscreen availability)
export const detectModernBrowser = (): boolean => {
    const supportsFetch = typeof window.fetch === 'function';
    const docEl = document as any;
    const supportsFs = !!(
        document.fullscreenEnabled ||
        docEl?.webkitFullscreenEnabled ||
        docEl?.mozFullScreenEnabled ||
        docEl?.msFullscreenEnabled
    );
    return supportsFetch && supportsFs;
};

// Immutable upsert for page heights array
export const upsertPageHeight = (prev: number[], page: number, height: number): number[] => {
    if (prev[page - 1] === height) return prev;
    const updated = [...prev];
    updated[page - 1] = height;
    return updated;
};

// Sum of heights up to (page - 1)
export const getPageOffsetFromHeights = (pageHeights: number[], page: number): number =>
    pageHeights.slice(0, page - 1).reduce((sum, h) => sum + (h || 0), 0);

// Sidebar virtual list height based on breakpoints
export const computeVirtualListSidebarHeight = (width: number, height: number): number => {
    if (width < BREAKPOINTS.desktopFullHD) return height * 0.7; // desktop full HD
    return height * 0.85; // Big screens (≥1920px)
};

const toBlobPart = (data: Uint8Array): ArrayBuffer => {
    const buffer = new ArrayBuffer(data.byteLength);
    new Uint8Array(buffer).set(data);
    return buffer;
};

// Open current PDF in a new tab
export const openPdfInNewTab = (pdf: PDFDocumentProxy | null): Promise<void> | undefined =>
    pdf?.getData().then((data) => {
        const url = URL.createObjectURL(new Blob([toBlobPart(data)], { type: FILE_TYPE_PDF }));
        openInNewTab(url);
    });

// Download current PDF to disk
export const downloadPdfFile = (pdf: PDFDocumentProxy | null, pdfTitle: string): Promise<void> | undefined =>
    pdf?.getData().then((data) => {
        const blob = new Blob([toBlobPart(data)], { type: FILE_TYPE_PDF });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${pdfTitle}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    });

// Set #page= hash and copy URL to clipboard
export const bookmarkCurrentPage = async (pageNum: number): Promise<void> => {
    const hash = `page=${pageNum}`;
    const newUrl = `${window.location.origin}${window.location.pathname}#${hash}`;
    window.location.hash = hash;
    try {
        await navigator.clipboard.writeText(newUrl);
    } catch (e) {
        // best-effort
        console.error('Copy failed', e);
    }
};
